import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import MakeupShopNav from '../components/MakeupShopNav';
import { inventoryApi } from '../api';

const INVENTORY_TYPES = [
  { value: '', label: '전체' },
  { value: 'COSMETIC', label: '화장품' },
  { value: 'TOOL', label: '도구' },
  { value: 'CONSUMABLE', label: '소모품' },
];

const TYPE_COLORS: Record<string, string> = {
  COSMETIC: 'bg-pink-100 text-pink-700',
  TOOL: 'bg-blue-100 text-blue-700',
  CONSUMABLE: 'bg-green-100 text-green-700',
};

const CHANGE_TYPE_LABELS: Record<string, string> = {
  IN: '입고',
  OUT: '출고',
  ADJUST: '조정',
};

interface Category {
  makeupInventoryCategoryIdx: number;
  name: string;
  type: string;
  orderNo: number;
}

interface InventoryItem {
  makeupInventoryItemIdx: number;
  itemName: string;
  brand: string;
  sku: string;
  currentStock: number;
  minStock: number | null;
  unit: string;
  unitPrice: number;
  expiryDt: string | null;
  memo: string;
  category?: Category;
}

interface StockLog {
  makeupInventoryLogIdx: number;
  changeType: string;
  quantity: number;
  beforeStock: number;
  afterStock: number;
  reason: string;
  createDt: string;
}

export default function Inventory() {
  const [activeTab, setActiveTab] = useState('');
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [lowStockItems, setLowStockItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  // 모달 상태
  const [showItemModal, setShowItemModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [stockLogs, setStockLogs] = useState<StockLog[]>([]);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);

  // 폼 상태
  const [newItem, setNewItem] = useState({
    categoryIdx: '', itemName: '', brand: '', sku: '', currentStock: 0,
    minStock: '', unit: '개', unitPrice: '', expiryDt: '', memo: '',
  });
  const [stockAdjust, setStockAdjust] = useState({ changeType: 'IN', quantity: 1, reason: '' });
  const [newCategory, setNewCategory] = useState({ name: '', type: 'COSMETIC' });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (activeTab) params.type = activeTab;
      const data = await inventoryApi.list(params);
      setItems(Array.isArray(data) ? data : data.items || []);
    } catch (error) {
      console.error('재고 목록 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await inventoryApi.categoryList();
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('카테고리 조회 실패:', error);
    }
  }, []);

  const fetchLowStock = useCallback(async () => {
    try {
      const data = await inventoryApi.lowStock();
      setLowStockItems(Array.isArray(data) ? data : data.items || []);
    } catch (error) {
      console.error('부족 재고 조회 실패:', error);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    fetchCategories();
    fetchLowStock();
  }, [fetchItems, fetchCategories, fetchLowStock]);

  // 품목 추가/수정
  const handleSaveItem = async () => {
    if (!newItem.itemName) return alert('품목명은 필수입니다.');
    try {
      const data = {
        ...newItem,
        categoryIdx: newItem.categoryIdx ? Number(newItem.categoryIdx) : undefined,
        currentStock: Number(newItem.currentStock),
        minStock: newItem.minStock ? Number(newItem.minStock) : undefined,
        unitPrice: newItem.unitPrice ? Number(newItem.unitPrice) : undefined,
        expiryDt: newItem.expiryDt || undefined,
      };
      if (editingItem) {
        await inventoryApi.update(editingItem.makeupInventoryItemIdx, data);
      } else {
        await inventoryApi.create(data);
      }
      setShowItemModal(false);
      setEditingItem(null);
      resetItemForm();
      fetchItems();
      fetchLowStock();
    } catch (error: any) {
      alert(`오류: ${error.message}`);
    }
  };

  const resetItemForm = () => {
    setNewItem({
      categoryIdx: '', itemName: '', brand: '', sku: '', currentStock: 0,
      minStock: '', unit: '개', unitPrice: '', expiryDt: '', memo: '',
    });
  };

  const handleEditItem = (item: InventoryItem) => {
    setEditingItem(item);
    setNewItem({
      categoryIdx: item.category?.makeupInventoryCategoryIdx?.toString() || '',
      itemName: item.itemName,
      brand: item.brand || '',
      sku: item.sku || '',
      currentStock: item.currentStock,
      minStock: item.minStock?.toString() || '',
      unit: item.unit || '개',
      unitPrice: item.unitPrice?.toString() || '',
      expiryDt: item.expiryDt?.split('T')[0] || '',
      memo: item.memo || '',
    });
    setShowItemModal(true);
  };

  const handleDeleteItem = async (id: number) => {
    if (!confirm('삭제하시겠습니까?')) return;
    try {
      await inventoryApi.delete(id);
      fetchItems();
      fetchLowStock();
    } catch (error: any) {
      alert(`오류: ${error.message}`);
    }
  };

  // 재고 조정
  const handleAdjustStock = async () => {
    if (!selectedItem || stockAdjust.quantity <= 0) return alert('수량을 입력해주세요.');
    try {
      await inventoryApi.adjustStock(selectedItem.makeupInventoryItemIdx, stockAdjust);
      setShowStockModal(false);
      setStockAdjust({ changeType: 'IN', quantity: 1, reason: '' });
      fetchItems();
      fetchLowStock();
    } catch (error: any) {
      alert(`오류: ${error.message}`);
    }
  };

  // 이력 조회
  const handleShowLog = async (item: InventoryItem) => {
    setSelectedItem(item);
    try {
      const data = await inventoryApi.stockLog(item.makeupInventoryItemIdx);
      setStockLogs(Array.isArray(data) ? data : data.items || []);
      setShowLogModal(true);
    } catch (error) {
      console.error('이력 조회 실패:', error);
    }
  };

  // 카테고리 추가
  const handleAddCategory = async () => {
    if (!newCategory.name) return alert('카테고리명은 필수입니다.');
    try {
      await inventoryApi.categoryCreate(newCategory);
      setShowCategoryModal(false);
      setNewCategory({ name: '', type: 'COSMETIC' });
      fetchCategories();
    } catch (error: any) {
      alert(`오류: ${error.message}`);
    }
  };

  const isLowStock = (item: InventoryItem) =>
    item.minStock !== null && item.currentStock <= item.minStock;

  const isExpired = (item: InventoryItem) => {
    if (!item.expiryDt) return false;
    return new Date(item.expiryDt) < new Date();
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <MakeupShopNav />

          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">재고관리</h1>
              <p className="text-sm text-gray-500 mt-1">화장품, 도구, 소모품 재고를 관리합니다</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowCategoryModal(true)}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-1"
              >
                <i className="ri-folder-add-line"></i> 카테고리
              </button>
              <button
                onClick={() => { resetItemForm(); setEditingItem(null); setShowItemModal(true); }}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1"
              >
                <i className="ri-add-line"></i> 품목 추가
              </button>
            </div>
          </div>

          {/* 부족 재고 알림 */}
          {lowStockItems.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-alarm-warning-line text-red-500 text-lg"></i>
                <span className="font-semibold text-red-700">재고 부족 알림 ({lowStockItems.length}건)</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {lowStockItems.map((item) => (
                  <span key={item.makeupInventoryItemIdx} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                    {item.itemName}: {item.currentStock}{item.unit || '개'} (최소 {item.minStock})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 타입 탭 */}
          <div className="flex gap-2 mb-6 border-b border-gray-200">
            {INVENTORY_TYPES.map((type) => (
              <button
                key={type.value}
                onClick={() => setActiveTab(type.value)}
                className={`px-5 py-3 text-sm font-medium transition-colors ${
                  activeTab === type.value
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>

          {/* 품목 목록 */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <i className="ri-archive-line text-5xl text-gray-300 mb-4 block"></i>
              <p className="text-gray-500">등록된 품목이 없습니다.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">품목명</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">브랜드</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">카테고리</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">현재고</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">최소재고</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">단가</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">유통기한</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((item) => {
                    const low = isLowStock(item);
                    const expired = isExpired(item);
                    return (
                      <tr key={item.makeupInventoryItemIdx} className={`hover:bg-gray-50 ${low ? 'bg-red-50/30' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {low && <i className="ri-error-warning-line text-red-500 text-sm"></i>}
                            <span className="text-sm font-medium text-gray-800">{item.itemName}</span>
                          </div>
                          {item.sku && <p className="text-xs text-gray-400">SKU: {item.sku}</p>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{item.brand || '-'}</td>
                        <td className="px-4 py-3">
                          {item.category ? (
                            <span className={`px-2 py-0.5 rounded text-xs ${TYPE_COLORS[item.category.type] || 'bg-gray-100 text-gray-700'}`}>
                              {item.category.name}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-sm font-bold ${low ? 'text-red-600' : 'text-gray-800'}`}>
                            {item.currentStock}{item.unit || ''}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-500">
                          {item.minStock !== null ? item.minStock : '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-600">
                          {item.unitPrice ? `${Number(item.unitPrice).toLocaleString()}원` : '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {item.expiryDt ? (
                            <span className={`text-xs ${expired ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                              {item.expiryDt.split('T')[0]}
                              {expired && ' (만료)'}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => { setSelectedItem(item); setStockAdjust({ changeType: 'IN', quantity: 1, reason: '' }); setShowStockModal(true); }}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="입출고"
                            >
                              <i className="ri-arrow-left-right-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => handleShowLog(item)}
                              className="p-1.5 text-gray-500 hover:bg-gray-100 rounded" title="이력"
                            >
                              <i className="ri-history-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => handleEditItem(item)}
                              className="p-1.5 text-gray-500 hover:bg-gray-100 rounded" title="수정"
                            >
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteItem(item.makeupInventoryItemIdx)}
                              className="p-1.5 text-red-400 hover:bg-red-50 rounded" title="삭제"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 품목 추가/수정 모달 */}
      {showItemModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {editingItem ? '품목 수정' : '품목 추가'}
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">품목명 <span className="text-red-500">*</span></label>
                  <input type="text" value={newItem.itemName} onChange={(e) => setNewItem({ ...newItem, itemName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">브랜드</label>
                  <input type="text" value={newItem.brand} onChange={(e) => setNewItem({ ...newItem, brand: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">카테고리</label>
                  <select value={newItem.categoryIdx} onChange={(e) => setNewItem({ ...newItem, categoryIdx: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">선택 안함</option>
                    {categories.map((c) => (
                      <option key={c.makeupInventoryCategoryIdx} value={c.makeupInventoryCategoryIdx}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                  <input type="text" value={newItem.sku} onChange={(e) => setNewItem({ ...newItem, sku: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">현재고</label>
                  <input type="number" value={newItem.currentStock} onChange={(e) => setNewItem({ ...newItem, currentStock: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">최소 재고</label>
                  <input type="number" value={newItem.minStock} onChange={(e) => setNewItem({ ...newItem, minStock: e.target.value })}
                    placeholder="미설정" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">단위</label>
                  <input type="text" value={newItem.unit} onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">단가</label>
                  <input type="number" value={newItem.unitPrice} onChange={(e) => setNewItem({ ...newItem, unitPrice: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">유통기한</label>
                  <input type="date" value={newItem.expiryDt} onChange={(e) => setNewItem({ ...newItem, expiryDt: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                <textarea rows={2} value={newItem.memo} onChange={(e) => setNewItem({ ...newItem, memo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowItemModal(false); setEditingItem(null); }} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50">취소</button>
              <button onClick={handleSaveItem} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                {editingItem ? '수정' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 입출고 모달 */}
      {showStockModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-2">재고 조정</h2>
            <p className="text-sm text-gray-500 mb-4">{selectedItem.itemName} (현재: {selectedItem.currentStock}{selectedItem.unit || ''})</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">유형</label>
                <div className="flex gap-2">
                  {(['IN', 'OUT', 'ADJUST'] as const).map((t) => (
                    <button key={t} onClick={() => setStockAdjust({ ...stockAdjust, changeType: t })}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border ${stockAdjust.changeType === t ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-600'}`}>
                      {CHANGE_TYPE_LABELS[t]}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">수량</label>
                <input type="number" min={1} value={stockAdjust.quantity}
                  onChange={(e) => setStockAdjust({ ...stockAdjust, quantity: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">사유</label>
                <input type="text" value={stockAdjust.reason} placeholder="예: 시술 사용, 신규 입고"
                  onChange={(e) => setStockAdjust({ ...stockAdjust, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowStockModal(false)} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg">취소</button>
              <button onClick={handleAdjustStock} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">적용</button>
            </div>
          </div>
        </div>
      )}

      {/* 이력 모달 */}
      {showLogModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">변동 이력</h2>
              <button onClick={() => setShowLogModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">{selectedItem.itemName}</p>
            {stockLogs.length === 0 ? (
              <p className="text-center text-gray-400 py-8">변동 이력이 없습니다.</p>
            ) : (
              <div className="space-y-2">
                {stockLogs.map((log) => (
                  <div key={log.makeupInventoryLogIdx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                      log.changeType === 'IN' ? 'bg-green-100 text-green-700'
                        : log.changeType === 'OUT' ? 'bg-red-100 text-red-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {CHANGE_TYPE_LABELS[log.changeType]}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm">
                        {log.beforeStock} → <span className="font-bold">{log.afterStock}</span>
                        <span className="text-gray-400 ml-1">({log.changeType === 'OUT' ? '-' : '+'}{log.quantity})</span>
                      </p>
                      {log.reason && <p className="text-xs text-gray-400">{log.reason}</p>}
                    </div>
                    <span className="text-xs text-gray-400">{new Date(log.createDt).toLocaleString('ko-KR')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 카테고리 모달 */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">카테고리 추가</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">카테고리명 <span className="text-red-500">*</span></label>
                <input type="text" value={newCategory.name} onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder="예: 파운데이션, 브러시" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">유형</label>
                <select value={newCategory.type} onChange={(e) => setNewCategory({ ...newCategory, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="COSMETIC">화장품</option>
                  <option value="TOOL">도구</option>
                  <option value="CONSUMABLE">소모품</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowCategoryModal(false)} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg">취소</button>
              <button onClick={handleAddCategory} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">추가</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
